import styles from './index.module.less';
import { App, Button } from 'antd';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { CommonBaseContent } from '../Layouts/CommonBackContent';
import InfluencersTopContent from './InfluencersTopContent';
import InfluencersCardItemContent from '../Common/InfluencersCardItemContent';
import { fetchHairstyleBloggerLabelsData, fetchHairstyleBloggerPageSearchData } from '@/lib/service';
import { HairShareBloggerInfoModel } from '@/types/hairstyle';
import FavoriteInfluencerAlert from './FavoriteInfluencerAlert';
import { BaseResponse } from '@/types/web.interface';
import HLabelContent from '../Common/HLabelContent';

const InfluencersContent = ({ data }: { data: any }) => {
  const divRef = useRef(null);
  const router = useRouter();
  const { locale } = router;
  const { t } = useTranslation('common');
  const { message } = App.useApp();
  const [showAlert, setShowAlert] = useState(false);

  const [nextPage, setNextPage] = useState(false);
  const [mainLoading, setMainLoading] = useState(false);
  const [influencersLoading, setInfluencersLoading] = useState(false);

  const [noData, setNoData] = useState(false);
  const [bloggerParams, setBloggerParams] = useState<any>({
    page_no: 1,
    page_size: 30,
    label: data.initialSearchParams?.label,
    search_word: data.initialSearchParams?.search_word
  });

  const [bloggerRes, setBloggerRes] = useState<BaseResponse<HairShareBloggerInfoModel[]>>(
    data.initialBloggerData || { data: [], total: 0 }
  );

  const [bloggerLabels, setBloggerLabels] = useState<string[]>(data.initialBloggerLabels || []);
  const [currentHairstyleLabel] = useState<string>(data.initialSearchParams?.label);

  const loadLabelsData = async () => {
    if (bloggerLabels?.length > 0) {
      return;
    }
    fetchHairstyleBloggerLabelsData(locale).then((res) => {
      if (res?.code === 0) {
        setBloggerLabels([...res?.data]);
      }
    });
  };

  useEffect(() => {
    if (bloggerLabels.length === 0) {
      loadLabelsData();
    }
  }, []);

  const loadBloggersMainList = (params: any, tempNoData: boolean) => {
    if (tempNoData) {
      return;
    }

    setInfluencersLoading(true);
    fetchHairstyleBloggerPageSearchData(params)
      .then((res) => {
        if (res?.code === 0) {
          setNoData(!res?.has_next || (res?.data?.length ?? 0) === 0);

          if (Number(params['page_no']) === 1) {
            setBloggerRes({ ...(res ?? {}) });
          } else {
            setBloggerRes({
              ...(res ?? {}),
              data: [...(bloggerRes?.data ?? []), ...(res?.data ?? [])]
            });
          }
        } else {
          loadBloggersError(res?.message);
        }
        setMainLoading(false);
        setNextPage(false);
        setInfluencersLoading(false);
      })
      .catch(() => {
        // loadBloggersError(e?.message);
        setMainLoading(false);
        setNextPage(false);
        setInfluencersLoading(false);
      });
  };

  const loadBloggersError = (error?: any) => {
    const pageNo = Number(bloggerParams['page_no']) - 1;
    const tempParams = { ...bloggerParams, page_no: pageNo < 1 ? 1 : pageNo };
    setBloggerParams(tempParams);
    message.error(error);
  };

  useEffect(() => {
    if (nextPage) {
      const tempParams = { ...bloggerParams, page_no: Number(bloggerParams['page_no']) + 1 };
      setBloggerParams(tempParams);
      loadBloggersMainList(tempParams, noData);
    }
  }, [nextPage]);

  useEffect(() => {
    if (
      currentHairstyleLabel !== data.initialSearchParams?.label ||
      bloggerParams?.search_word !== data.initialSearchParams?.search_word ||
      !data.initialBloggerData
    ) {
      const tempParams = { ...bloggerParams, label: currentHairstyleLabel };
      setBloggerParams(tempParams);
      loadBloggersMainList(tempParams, noData);
    }
  }, [currentHairstyleLabel]);

  useEffect(() => {
    const handleScroll = () => {
      const div = divRef.current;

      if (!div) return;

      const { bottom } = div.getBoundingClientRect();
      const distanceToBottom = window.innerHeight - bottom;

      if (distanceToBottom > -200) {
        if (mainLoading || noData) {
          return;
        }
        setNextPage(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div ref={divRef}>
      {/* 隐藏的 H 标签容器 */}
      <HLabelContent hlabelPath={'influencers'} />

      <CommonBaseContent style={{ maxWidth: '1680px', margin: '0 auto' }}>
        <InfluencersTopContent
          bloggerCount={bloggerRes?.total}
          hairstyleLabels={[]}
          bloggerLabels={bloggerLabels}
          searchEmpty={bloggerParams?.search_word?.length > 0}
          onChangeType={(type, item, val) => {
            setNoData(false);
            setMainLoading(false);

            let tempParams;

            if (val) {
              tempParams = {
                page_no: 1,
                page_size: 30,
                search_word: val
              };
            } else {
              tempParams = {
                page_no: 1,
                page_size: 30,
                label: item
              };
            }

            setBloggerParams({ ...tempParams });
            loadBloggersMainList(tempParams, false);
          }}
          onBloggerClick={() => setShowAlert(true)}
          type={'influencers'}
        />

        <>
          {bloggerRes?.data?.length > 0 ? (
            <div className={styles.content}>
              {(bloggerRes?.data ?? []).map((item, index) => {
                return <InfluencersCardItemContent key={`${item?.nickname}-${index}-${item?.avatar}`} item={item} />;
              })}
            </div>
          ) : (
            <>
              {!influencersLoading && (
                <div className={styles.emptyContainer}>
                  <div className={styles.title}>{t('Influencer Not Found')}</div>
                  <p className={styles.desc}>
                    {t(
                      "We couldn't find the hairstyle influencer you're looking for. If you'd like to see this influencer on Hairpaca, please submit their profile to us for consideration."
                    )}
                  </p>
                  <Button className={styles.btn} onClick={() => setShowAlert(true)}>
                    {t('Submit Your Favorite Influencer')}
                  </Button>
                  <Button className={styles.btn} onClick={() => setShowAlert(true)}>
                    {t('Submit Your Favorite Influencer')}
                  </Button>
                </div>
              )}
            </>
          )}

          <FavoriteInfluencerAlert
            open={showAlert}
            handleClose={() => {
              setShowAlert(false);
            }}
          />
        </>
      </CommonBaseContent>
    </div>
  );
};

export default InfluencersContent;
